import {
  DocModeProvider,
  TelemetryProvider,
} from '@blocksuite/affine-shared/services';
import type { AffineInlineEditor } from '@blocksuite/affine-shared/types';
import type { BlockStdScope, Command, TextSelection } from '@blocksuite/std';
import {
  INLINE_ROOT_ATTR,
  type InlineRange,
  type InlineRootElement,
} from '@blocksuite/std/inline';

function openInlineLatexEditor(
  inlineEditor: AffineInlineEditor,
  index: number
) {
  inlineEditor
    .waitForUpdate()
    .then(async () => {
      await inlineEditor.waitForUpdate();

      const textPoint = inlineEditor.getTextPoint(index);
      if (!textPoint) return;
      const [text] = textPoint;
      const latexNode = text.parentElement?.closest('affine-latex-node');
      if (!latexNode) return;
      latexNode.toggleEditor();
    })
    .catch(console.error);
}

function getSingleBlockInlineRange(
  textSelection: TextSelection
): InlineRange | null {
  if (textSelection.to) {
    return null;
  }

  return {
    index: textSelection.from.index,
    length: textSelection.from.length,
  };
}

function getNativeRange(range?: Range): Range | null {
  if (range) {
    return range;
  }

  const selection = document.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return null;
  }

  return selection.getRangeAt(0);
}

function getInlineRoot(node: Node): InlineRootElement | null {
  const element = node instanceof Element ? node : node.parentElement;
  return element?.closest<InlineRootElement>(`[${INLINE_ROOT_ATTR}]`) ?? null;
}

function getNativeInlineLatexTarget(
  std: BlockStdScope,
  range: Range
): {
  inlineEditor: AffineInlineEditor;
  inlineRange: InlineRange;
  inlineRoot: InlineRootElement;
} | null {
  if (range.collapsed) {
    return null;
  }

  const startInlineRoot = getInlineRoot(range.startContainer);
  const endInlineRoot = getInlineRoot(range.endContainer);
  if (
    !startInlineRoot ||
    startInlineRoot !== endInlineRoot ||
    !std.host.contains(startInlineRoot) ||
    startInlineRoot.closest('latex-editor-menu')
  ) {
    return null;
  }

  const inlineEditor = startInlineRoot.inlineEditor;
  if (
    !inlineEditor ||
    inlineEditor.isReadonly ||
    startInlineRoot.contentEditable !== 'true'
  ) {
    return null;
  }

  const selectedInlineRoots = Array.from<InlineRootElement>(
    std.host.querySelectorAll(`[${INLINE_ROOT_ATTR}]`)
  ).filter(root => range.intersectsNode(root));
  if (
    selectedInlineRoots.length !== 1 ||
    selectedInlineRoots[0] !== startInlineRoot
  ) {
    return null;
  }

  const inlineRange = inlineEditor.toInlineRange(range);
  if (
    !inlineRange ||
    inlineRange.length === 0 ||
    !inlineEditor.isValidInlineRange(inlineRange)
  ) {
    return null;
  }

  return { inlineEditor, inlineRange, inlineRoot: startInlineRoot };
}

function insertInlineLatexAtRange(
  std: BlockStdScope,
  inlineEditor: AffineInlineEditor,
  inlineRange: InlineRange,
  source: Element,
  openEditor: boolean
) {
  const latex =
    inlineRange.length === 0
      ? ''
      : inlineEditor.yTextString.slice(
          inlineRange.index,
          inlineRange.index + inlineRange.length
        );

  inlineEditor.insertText(inlineRange, ' ', { latex });
  inlineEditor.setInlineRange({
    index: inlineRange.index,
    length: 1,
  });

  const mode = std.get(DocModeProvider).getEditorMode() ?? 'page';
  const ifEdgelessText = source.closest('affine-edgeless-text');
  std.getOptional(TelemetryProvider)?.track('Latex', {
    from:
      mode === 'page'
        ? 'doc'
        : ifEdgelessText
          ? 'edgeless text'
          : 'edgeless note',
    page: mode === 'page' ? 'doc' : 'edgeless',
    segment: mode === 'page' ? 'doc' : 'whiteboard',
    module: 'inline equation',
    control: 'create inline equation',
  });

  if (openEditor) {
    openInlineLatexEditor(inlineEditor, inlineRange.index + 1);
  }
}

export const insertInlineLatex: Command<{
  currentTextSelection?: TextSelection;
  textSelection?: TextSelection;
}> = (ctx, next) => {
  const textSelection = ctx.textSelection ?? ctx.currentTextSelection;
  if (!textSelection) return;

  const blockComponent = ctx.std.view.getBlock(textSelection.from.blockId);
  if (!blockComponent) return;

  const richText = blockComponent.querySelector('rich-text');
  if (!richText) return;

  const inlineEditor = richText.inlineEditor;
  if (!inlineEditor) return;

  const inlineRange = getSingleBlockInlineRange(textSelection);
  if (!inlineRange) return;

  insertInlineLatexAtRange(
    ctx.std,
    inlineEditor,
    inlineRange,
    blockComponent,
    textSelection.isCollapsed()
  );

  next();
};

export const insertInlineLatexFromNativeSelection: Command<{
  range?: Range;
}> = (ctx, next) => {
  const range = getNativeRange(ctx.range);
  if (!range) return;

  const target = getNativeInlineLatexTarget(ctx.std, range);
  if (!target) return;

  insertInlineLatexAtRange(
    ctx.std,
    target.inlineEditor,
    target.inlineRange,
    target.inlineRoot,
    false
  );

  next();
};
